import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';

import { MessstellenListeSummary } from '../../../Models/messstelle.model';
import { OfflineListeStatus } from '../../../Models/offline.model';
import { OfflineDownloadService } from '../../../Services/offline/offline-download.service';

/**
 * Der Offline-Block einer Inbetriebnahme-Kachel: herunterladen, aktualisieren,
 * entfernen – samt Stand, letzter Synchronisierung und offenen Änderungen.
 *
 * Bewusst eine eigene Komponente und nicht zweimal derselbe Markup-Block:
 * die Kachel gibt es auf der Inbetriebnahme-Übersicht UND auf der
 * Projekt-Detailseite. Zwei Kopien wären zwei Orte, an denen eine spätere
 * Änderung vergessen werden kann – und ausgerechnet hier hängt daran, ob die
 * Arbeit eines Tages auf dem Gerät landet oder nicht.
 */
@Component({
  selector: 'app-offline-kachel',
  templateUrl: './offline-kachel.component.html',
  styleUrls: ['./offline-kachel.component.css']
})
export class OfflineKachelComponent implements OnInit, OnDestroy {

  /** Die Inbetriebnahme, um die es geht. */
  @Input({ required: true }) inbetriebnahme!: MessstellenListeSummary;

  /**
   * Arbeitet die aufrufende Seite gerade aus dem lokalen Bestand?
   *
   * Dann lassen sich Herunterladen und Aktualisieren nicht anbieten – beides
   * braucht zwingend eine Verbindung. Entfernen bleibt möglich, sofern nichts
   * mehr aussteht.
   */
  @Input() seiteIstOffline = false;

  /**
   * Meldet, dass sich am lokalen Bestand etwas geändert hat. Die aufrufende
   * Seite kann daraufhin neu laden.
   */
  @Output() geaendert = new EventEmitter<void>();

  status: OfflineListeStatus | null = null;

  private abo?: Subscription;

  constructor(
    private download: OfflineDownloadService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.abo = this.download.offlineStatus$.subscribe(alle => {
      this.status =
        alle.find(s => s.listeId === this.inbetriebnahme?.id) ?? null;
    });

    void this.download.aktualisiereStatus();
  }

  ngOnDestroy(): void {
    this.abo?.unsubscribe();
  }

  get istVerfuegbar(): boolean {
    return !!this.status;
  }

  get laeuft(): boolean {
    return this.download.laeuftGerade(this.inbetriebnahme?.id);
  }

  /** Ausstehende Änderungen plus offene Konflikte – beides hält vom Löschen ab. */
  get offen(): number {
    return (this.status?.ausstehend ?? 0) + (this.status?.konflikte ?? 0);
  }

  // ===================================================================
  // AKTIONEN
  // ===================================================================

  /**
   * Lädt die komplette Inbetriebnahme auf das Gerät.
   *
   * Braucht zwingend eine Verbindung – das ist der Sinn der Sache: was vor
   * Ort gebraucht wird, muss vorher da sein.
   */
  async verfuegbarMachen(event: Event): Promise<void> {
    event.stopPropagation();

    const ergebnis = await this.download.machOfflineVerfuegbar(
      this.inbetriebnahme.id
    );

    this.melde(
      ergebnis.ok,
      ergebnis.ok
        ? `„${this.inbetriebnahme.name}" ist jetzt offline verfügbar.`
        : ergebnis.meldung
    );
  }

  /**
   * Holt den aktuellen Serverstand.
   *
   * Der Dienst schickt zuerst alles Erfasste hoch und lädt erst danach –
   * andernfalls würde der Download die eigenen, noch nicht übertragenen
   * Prüfungen überschreiben.
   */
  async aktualisieren(event: Event): Promise<void> {
    event.stopPropagation();

    const ergebnis = await this.download.aktualisiere(this.inbetriebnahme.id);

    this.melde(
      ergebnis.ok,
      ergebnis.ok ? 'Offline-Daten aktualisiert.' : ergebnis.meldung
    );

    if (ergebnis.ok) {
      this.geaendert.emit();
    }
  }

  /**
   * Entfernt den lokalen Bestand – aber nur, wenn nichts mehr aussteht.
   *
   * Die Warteschlange ist der einzige Ort, an dem eine noch nicht übertragene
   * Prüfung existiert; sie mitzulöschen hieße, die Arbeit zu löschen.
   */
  async entfernen(event: Event): Promise<void> {
    event.stopPropagation();

    if (this.offen === 0
      && !confirm(`Offline-Daten von „${this.inbetriebnahme.name}" entfernen?`)) {
      return;
    }

    const ergebnis = await this.download.entferne(this.inbetriebnahme.id);

    this.melde(
      ergebnis.ok,
      ergebnis.ok ? 'Offline-Daten entfernt.' : ergebnis.meldung
    );
  }

  private melde(ok: boolean, text: string): void {
    this.snackBar.open(text, 'OK', { duration: ok ? 3000 : 7000 });
  }
}
